// Sidecar schema and migrations (§5, §8). `<original name>.epb.json` next to each photo.
//
// Schema history:
//   1 — initial.
//   2 — settings.effects (vignette, grain, split toning; §11.19). Settings.v 1 → 2.
//   3 — settings.upscale (M13). Settings.v 2 → 3.
//   4 — M22 colour-range and object masks; mask adjustments hue, vibrance, clarity. Settings.v 3 → 4. An older
//       build refuses a schema-4 sidecar instead of drawing the new masks wrong.
// `migrate()` is the single entry point: it validates, upgrades older schemas step by step, fills
// fields added later with defaults (deep merge), and refuses schemas newer than this build.
import { defaultEffects, defaultSettings, defaultUpscale } from './defaults';
import { zeroAdj } from '../features/localTools';
import type { Settings, Sidecar } from './types';

export const SIDECAR_SCHEMA = 4;
export const SIDECAR_SUFFIX = '.epb.json';
export const MASK_SUFFIX = '.epb.mask.png';
export const APP_VERSION = '0.1.0';

export const sidecarName = (photoName: string) => photoName + SIDECAR_SUFFIX;
export const maskName = (photoName: string) => photoName + MASK_SUFFIX;

export class SidecarError extends Error {}

type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

/** Deep-merge `value` over `base`, keeping only keys known to `base` (arrays and leaves replace). */
function mergeKnown<T>(base: T, value: unknown): T {
  if (Array.isArray(base)) return (Array.isArray(value) ? value : base) as T;
  if (base !== null && typeof base === 'object') {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return base;
    const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
    for (const k of Object.keys(out))
      if (k in (value as object)) out[k] = mergeKnown(out[k], (value as Record<string, unknown>)[k]);
    // Optional keys not present in defaults (background.key, background.maskCacheKey) pass through.
    for (const k of Object.keys(value as object))
      if (!(k in out)) out[k] = (value as Record<string, unknown>)[k];
    return out as T;
  }
  if (typeof base === 'number')
    return (typeof value === 'number' && Number.isFinite(value) ? value : base) as T;
  if (typeof base === 'boolean') return (typeof value === 'boolean' ? value : base) as T;
  if (typeof base === 'string') return (typeof value === 'string' ? value : base) as T;
  return (value === undefined ? base : value) as T;
}

export function normalizeSettings(raw: unknown): Settings {
  const s = mergeKnown(defaultSettings(), raw);
  s.v = 4;
  // Mask adjustments added later (M22 hue, vibrance, clarity) start at 0 (arrays replace in mergeKnown).
  s.masks = s.masks.map((m) => ({ ...m, adj: { ...zeroAdj(), ...m.adj } }));
  return s;
}

const MIGRATIONS: Record<number, (s: Record<string, Json>) => Record<string, Json>> = {
  // 1 → 2: effects did not exist; every value starts at its default (no effect), so the look is unchanged.
  1: (obj) => {
    const settings = (obj.settings ?? {}) as Record<string, Json>;
    return {
      ...obj,
      schema: 2,
      settings: { ...settings, v: 2, effects: defaultEffects() as unknown as Json },
    };
  },
  // 2 → 3: upscale did not exist; factor 1 = off, so the look and size are unchanged.
  2: (obj) => {
    const settings = (obj.settings ?? {}) as Record<string, Json>;
    return {
      ...obj,
      schema: 3,
      settings: { ...settings, v: 3, upscale: defaultUpscale() as unknown as Json },
    };
  },
  // 3 → 4: the new mask kinds and adjustments are additions; existing masks get hue/vibrance/clarity 0
  // (normalizeSettings), so the look is unchanged.
  3: (obj) => {
    const settings = (obj.settings ?? {}) as Record<string, Json>;
    return { ...obj, schema: 4, settings: { ...settings, v: 4 } };
  },
};

export function migrate(raw: unknown): Sidecar {
  if (raw === null || typeof raw !== 'object') throw new SidecarError('sidecar is not an object');
  let obj = raw as Record<string, Json>;
  if (obj.app !== 'eat-photo-booth') throw new SidecarError('not an EAT Photo Booth sidecar');
  let schema = typeof obj.schema === 'number' ? obj.schema : NaN;
  if (!Number.isInteger(schema) || schema < 1) throw new SidecarError(`invalid schema ${String(obj.schema)}`);
  if (schema > SIDECAR_SCHEMA)
    throw new SidecarError(`sidecar schema ${schema} is newer than this app (${SIDECAR_SCHEMA})`);
  while (schema < SIDECAR_SCHEMA) {
    const step = MIGRATIONS[schema];
    if (!step) throw new SidecarError(`no migration from schema ${schema}`);
    obj = step(obj);
    schema++;
  }
  const src = (obj.source ?? {}) as Record<string, Json>;
  if (typeof src.name !== 'string' || typeof src.size !== 'number' || typeof src.lastModified !== 'number')
    throw new SidecarError('sidecar source is missing name/size/lastModified');
  const rating = typeof obj.rating === 'number' ? Math.min(5, Math.max(0, Math.round(obj.rating))) : 0;
  return {
    schema: 4,
    app: 'eat-photo-booth',
    appVersion: typeof obj.appVersion === 'string' ? obj.appVersion : 'unknown',
    source: { name: src.name, size: src.size, lastModified: src.lastModified },
    settings: normalizeSettings(obj.settings),
    rating,
    rejected: obj.rejected === true,
    updatedAt: typeof obj.updatedAt === 'string' ? obj.updatedAt : new Date(0).toISOString(),
  };
}

export function parseSidecar(text: string): Sidecar {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new SidecarError('sidecar is not valid JSON');
  }
  return migrate(raw);
}

export function makeSidecar(
  source: Sidecar['source'],
  settings: Settings,
  rating: number,
  rejected: boolean,
  now = new Date(),
): Sidecar {
  return {
    schema: 4,
    app: 'eat-photo-booth',
    appVersion: APP_VERSION,
    source,
    settings,
    rating,
    rejected,
    updatedAt: now.toISOString(),
  };
}

/** A sidecar applies only to the exact file it was written for (§8). */
export function sidecarMatches(sc: Sidecar, file: { size: number; lastModified: number }): boolean {
  return sc.source.size === file.size && sc.source.lastModified === file.lastModified;
}
